import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  IconButton,
} from "@mui/material";
import { Info } from "@mui/icons-material";
import ChartGrid from "../chart/ChartGrid";
import StitchPalette from "./StitchPalette";
import PatternCompilation from "../chart/PatternCompilation";
import GridSettings from "./GridSettings";
import PatternActions from "./PatternActions";
import ExportModal from "./ExportModal";
import SubmitToGalleryModal from "./SubmitToGalleryModal";
import {
  compilePattern,
  compilePatternToText,
} from "../../services/patternCompiler";
import "./ChartMakerLayout.css";

const ChartMakerLayout = ({
  // Grid and chart data
  gridSize,
  chart,
  selectedSymbol,
  // Add state for the click handler
  selectedStitchClickHandler,
  setSelectedStitchClickHandler,

  // Event handlers
  onSizeChange,
  onCreateGrid,
  onChartChange,
  onSymbolSelect,
  onPatternTitleChange,
  onSavePattern,
  onClearGrid,
  onExport,
  onInfoModalOpen,

  // State
  patternTitle,
  exportModalOpen,
  onExportModalClose,
  onImportPattern,
  isGeneratingChart,
}) => {
  const [submitOpen, setSubmitOpen] = useState(false);
  const [inTheRound, setInTheRound] = useState(false);
  return (
    <Box className="fade-in-up">
      {/* Header Section */}
      <Container maxWidth="lg" className="chart-maker-container">
        <Box className="center mb-4">
          <Typography
            variant="h2"
            component="h1"
            color="primary"
            className="chart-maker-title"
          >
            Chart Maker
          </Typography>
          <Typography
            variant="subtitle1"
            color="text.secondary"
            className="mt-1"
          >
            Create your custom knitting pattern with an intuitive grid system
          </Typography>
          <Box component="hr" className="hr-accent" aria-hidden="true" />
        </Box>
      </Container>

      {/* Grid Settings, Pattern Actions, and Build Your Pattern */}
      <Container maxWidth="lg" className="mb-4">
        <div className="row g-4">
          <div className="col-12 col-md-6">
            <Paper variant="woven" className="paper paper--woven h-100">
              <GridSettings
                gridSize={gridSize}
                onSizeChange={onSizeChange}
                onCreateGrid={onCreateGrid}
                isGeneratingChart={isGeneratingChart}
                inTheRound={inTheRound}
                onInTheRoundChange={setInTheRound}
              />
            </Paper>
          </div>

          <div className="col-12 col-md-6">
            <Paper variant="woven" className="paper paper--woven h-100">
              <PatternActions
                patternTitle={patternTitle}
                onPatternTitleChange={onPatternTitleChange}
                onSavePattern={onSavePattern}
                onClearGrid={onClearGrid}
                onExport={onExport}
                onSubmitToGallery={() => setSubmitOpen(true)}
                gridSize={gridSize}
              />
            </Paper>
          </div>

          <div className="col-12">
            <Paper variant="woven" className="paper paper--woven">
              <Box className="p-4">
                <Box className="chart-maker-grid-header mb-4">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Typography variant="h4" color="primary">
                      Build Your Pattern
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={onInfoModalOpen}
                      className="chart-maker-info-button"
                    >
                      <Info fontSize="small" />
                    </IconButton>
                  </div>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    className="mb-3"
                  >
                    Select a stitch symbol from the palette, then click or drag
                    on the grid to paint.
                  </Typography>
                  <StitchPalette
                    onSymbolSelect={onSymbolSelect}
                    selectedSymbol={selectedSymbol}
                    chart={chart}
                    onSelectedStitchClickRef={setSelectedStitchClickHandler}
                  />
                </Box>

                <Box className="chart-maker-grid-container">
                  <ChartGrid
                    chart={chart}
                    onChartChange={onChartChange}
                    readOnly={false}
                    selectedSymbol={selectedSymbol}
                    inTheRound={inTheRound}
                    onSelectedStitchClick={selectedStitchClickHandler || undefined}
                  />
                </Box>
              </Box>
            </Paper>
          </div>
        </div>
      </Container>

      {/* Written Instructions Section */}
      <Container maxWidth="lg" className="mt-4">
        <div className="row">
          <div className="col-12">
            <PatternCompilation 
              chart={chart} 
              patternTitle={patternTitle}
              inTheRound={inTheRound}
            />
          </div>
        </div>
      </Container>

      {/* Export Modal */}
      <ExportModal
        open={exportModalOpen}
        onClose={onExportModalClose}
        chart={chart}
        patternTitle={patternTitle}
        compiledInstructions={chart ? compilePatternToText(compilePattern(chart, false, inTheRound), inTheRound) : ""}
        onImportPattern={onImportPattern}
        inTheRound={inTheRound}
      />

      <SubmitToGalleryModal
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        chart={chart}
        patternTitle={patternTitle}
        compiledInstructions={chart ? compilePatternToText(compilePattern(chart, false, inTheRound), inTheRound) : ""}
      />
    </Box>
  );
};

export default ChartMakerLayout;
